/* G Decor Wishlist + Heart system v8 — saves the price the CUSTOMER PAYS
   ------------------------------------------------------------------
   v8 change (16-Aug-2026) — THE WISHLIST WAS STORING THE LIST PRICE.

   Two separate defects, both fixed here:

   1. CAPTURE. The card sections render data-price="{{ product.price | money }}",
      which is the LIST price. Shopify automatic discounts are invisible to Liquid,
      so under the clearance ladder the cart charges less than that. A shopper
      hearted an item showing GBP 4.80 and came back to a saved GBP 5.99. We now
      read the figure the card is actually displaying (.gd-product__price-now), and
      on the PDP the first money string inside .price when the clearance chip is
      present. Falls back to data-price in every unknown case.

   2. STALENESS — the bigger half. A price captured at save time can NEVER stay
      right: the ladder steps on 1 September and every stored figure goes stale on
      the spot. So the wishlist page no longer treats the stored price as truth. On
      every render it re-derives the current paid price from the live product JSON
      (/products/<handle>.js) and the ladder settings injected by
      snippets/gd-wishlist-clearance-config.liquid, then quietly corrects storage so
      the server copy and the Klaviyo value stop drifting too.

   FAILS SAFE: no config, a failed fetch, an unknown product or the master switch
   off all leave the stored price on screen exactly as before. Showing yesterday's
   price is a nuisance; showing a discount that does not exist is a false claim.

   v7 (unchanged): the PDP wishlist control is a compact text-link with a brass
   hairline underline, positioned inside the price block.

   Storage model:
   - Logged out → localStorage is source of truth
   - Logged in  → server (via /apps/wishlist App Proxy) is source of truth,
                  localStorage is a fast-load cache that mirrors the server

   Login sync — merge local + server (union by handle) on every sign-in so
   items added while signed out are preserved. Network errors degrade
   gracefully — local cache is always usable.
*/
(function(){
  var KEY              = 'gd_wishlist_v1';
  var EVER_SYNCED_KEY  = 'gd_wl_ever_synced_v1';
  var ENDPOINT         = '/apps/wishlist';
  var isLoggedIn       = false;
  var pushDebounce     = null;

  var LABEL_ADD   = 'Save to wishlist';
  var LABEL_SAVED = 'Saved to wishlist';

  function read(){
    try { return JSON.parse(localStorage.getItem(KEY) || '{"email":null,"items":[]}'); }
    catch(e){ return {email:null, items:[]}; }
  }
  function writeLocal(data){
    try { localStorage.setItem(KEY, JSON.stringify(data)); } catch(e){}
    document.dispatchEvent(new CustomEvent('gd:wishlist:updated', {detail: data}));
  }
  function write(data){
    writeLocal(data);
    if (isLoggedIn) schedulePush();
  }

  function schedulePush(){
    clearTimeout(pushDebounce);
    pushDebounce = setTimeout(pushNow, 350);
  }
  function pushNow(cb){
    if (!isLoggedIn) { if(cb) cb(false); return; }
    var items = read().items;
    fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function(res){ if(cb) cb(res && res.ok); })
      .catch(function(){ if(cb) cb(false); });
  }
  function loadFromServer(cb){
    if (!isLoggedIn) { cb(null); return; }
    fetch(ENDPOINT, { credentials: 'same-origin' })
      .then(function(res){ return res.json(); })
      .then(function(data){
        if (data && data.logged_in === true && Array.isArray(data.items)) cb(data.items);
        else cb(null);
      })
      .catch(function(){ cb(null); });
  }

  // Merge two wishlists — union by handle, local items first (preserves recent additions).
  function mergeWishlists(localItems, serverItems){
    var seen = {};
    var merged = [];
    (localItems || []).forEach(function(it){
      if (it && it.handle && !seen[it.handle]) {
        seen[it.handle] = true;
        merged.push(it);
      }
    });
    (serverItems || []).forEach(function(it){
      if (it && it.handle && !seen[it.handle]) {
        seen[it.handle] = true;
        merged.push(it);
      }
    });
    return merged;
  }
  function sameHandleSet(a, b){
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    var ah = a.map(function(i){ return i.handle; }).sort();
    var bh = b.map(function(i){ return i.handle; }).sort();
    for (var i = 0; i < ah.length; i++) {
      if (ah[i] !== bh[i]) return false;
    }
    return true;
  }

  function syncWithServer(){
    if (!isLoggedIn) return;
    loadFromServer(function(serverItems){
      if (serverItems === null) return; // network error — keep local as is
      var local = read();
      var merged = mergeWishlists(local.items, serverItems);
      writeLocal({ email: local.email, items: merged });
      try { localStorage.setItem(EVER_SYNCED_KEY, '1'); } catch(e){}
      if (!sameHandleSet(merged, serverItems)) {
        pushNow();
      }
    });
  }

  function isIn(handle){ return read().items.some(function(i){ return i.handle === handle; }); }
  function toggle(item){
    var data = read();
    var idx = data.items.findIndex(function(i){ return i.handle === item.handle; });
    if(idx > -1){ data.items.splice(idx, 1); } else { data.items.unshift(item); }
    write(data);
    return idx === -1;
  }
  function remove(handle){
    var data = read();
    data.items = data.items.filter(function(i){ return i.handle !== handle; });
    write(data);
  }
  function track(evt, props){
    try { window._learnq = window._learnq || []; window._learnq.push(['track', evt, props||{}]); } catch(e){}
  }

  /* ====================================================================
     v8 — THE CLEARANCE LADDER, MIRRORED FROM snippets/gd-clearance.liquid
     ====================================================================
     Same rules, same order, same arithmetic. Read that file before changing
     anything here; the two must agree or the wishlist will advertise a price
     the cart does not charge.

     Shopify rounds the DISCOUNT DOWN to the penny and subtracts it. It does NOT
     round the final price. 1099 * 20 / 100 = 219.8 -> floor 219 -> 880 (GBP 8.80).
     Rounding to nearest would give 8.79, which is wrong on roughly half of all
     products, and the wrong penny is the one the customer notices at checkout.
  */
  function ladder(listCents, compareAtCents, tags){
    var c = window.gdClearanceCfg;
    if(!c || c.show !== true) return null;              // no config / switched off
    if(!(listCents > 0)) return null;
    // A variant still carrying a compare-at is priced by the old hard-cut
    // mechanism and already shows a strikethrough. The Liquid snippet stands
    // down in that case, so we do too.
    if(compareAtCents && compareAtCents > listCents) return null;

    var t1 = parseInt(c.t1, 10) || 0;
    var t2 = parseInt(c.t2, 10) || 0;
    var t3 = parseInt(c.t3, 10) || 0;
    var now = Math.floor(Date.now() / 1000);
    var phase = 0;
    if (t1 > 0 && t2 > t1 && now >= t1 && now < t2) phase = 1;
    else if (t2 > 0 && t3 > t2 && now >= t2 && now < t3) phase = 2;
    if (!phase) return null;                            // outside all phases

    var tl = (tags || []).map(function(t){ return String(t).trim(); });

    // Exclusions are checked FIRST and win outright. Track C personalised carries
    // no discount at all and a badge claiming one would be a false price claim on
    // the biggest product family in the business.
    var ex = String(c.exclude || '').split(',');
    for (var i = 0; i < ex.length; i++) {
      var t = ex[i].trim();
      if (t && tl.indexOf(t) > -1) return null;
    }

    var trackName = null;
    if (c.bulky && tl.indexOf(String(c.bulky).trim()) > -1) trackName = 'bulky';
    else if (c.dense && tl.indexOf(String(c.dense).trim()) > -1) trackName = 'dense';
    if (!trackName) return null;

    var pct = parseFloat(
      phase === 1 ? (trackName === 'bulky' ? c.p1b : c.p1d)
                  : (trackName === 'bulky' ? c.p2b : c.p2d)
    ) || 0;
    if (!(pct > 0 && pct < 100)) return null;

    var off = Math.floor(listCents * pct / 100);
    var nw  = listCents - off;
    if (nw <= 0 || nw >= listCents) return null;
    return { cents: nw, pct: Math.round(pct) };
  }

  function moneyFmt(cents){ return '£' + (Number(cents) / 100).toFixed(2); }

  // v8 — read the price the shopper can actually see on the page, rather than the
  // list price Liquid baked into data-price.
  function paidPriceFromDom(btn){
    try {
      var card = btn.closest('.gd-product');
      if (card) {
        var now = card.querySelector('.gd-product__price--clearance .gd-product__price-now');
        if (now && now.textContent.trim()) return now.textContent.trim();
      }
      if (btn.classList.contains('gd-wl-btn--pdp')) {
        var priceBlock = document.querySelector('.price');
        if (priceBlock && priceBlock.querySelector('.gd-clearance-off')) {
          // The sale price renders before the struck regular price, so the first
          // money string in the block is the one the cart will charge.
          var m = priceBlock.textContent.match(/[£$€]\s?[0-9][0-9,]*(\.[0-9]{2})?/);
          if (m) return m[0].replace(/\s/g, '');
        }
      }
    } catch(e){}
    return null;
  }

  function showToast(msg){
    var t = document.getElementById('gd-toast');
    if(!t){ t = document.createElement('div'); t.id='gd-toast'; t.className='gd-toast'; document.body.appendChild(t); }
    t.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(t._tm);
    t._tm = setTimeout(function(){ t.classList.remove('is-visible'); }, 2400);
  }

  function setBtnLabel(btn, active){
    var lbl = btn.querySelector('.gd-wl-btn__label');
    if(lbl){ lbl.textContent = active ? LABEL_SAVED : LABEL_ADD; }
  }

  function commitToggle(btn, item){
    var added = toggle(item);
    btn.classList.toggle('is-active', added);
    btn.setAttribute('aria-label', added ? 'Remove from wishlist' : 'Save to wishlist');
    btn.setAttribute('aria-pressed', added ? 'true' : 'false');
    setBtnLabel(btn, added);
    document.querySelectorAll('.gd-wl-heart[data-handle="' + item.handle + '"]').forEach(function(b){
      b.classList.toggle('is-active', added);
      b.setAttribute('aria-pressed', added ? 'true' : 'false');
      setBtnLabel(b, added);
    });
    if(added){
      track('Added to Wishlist', {ProductName: item.title, ProductURL: item.url, $value: parseFloat(String(item.price).replace(/[^0-9.]/g,'')) || 0});
      showToast(isLoggedIn ? 'Saved to your account' : 'Saved to wishlist');
    } else {
      showToast('Removed from wishlist');
    }
  }

  function getItemFromBtn(btn){
    var d = btn.dataset;
    var paid = paidPriceFromDom(btn);   // v8 — what the cart will charge, if visible
    return {
      handle: d.handle,
      title:  d.title,
      image:  d.image,
      price:  paid || d.price,
      list_price: d.price,              // kept so a "was" figure is never invented
      url:    d.url
    };
  }

  document.addEventListener('click', function(e){
    var btn = e.target.closest('.gd-wl-heart');
    if(!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var item = getItemFromBtn(btn);
    if(!item.handle) return;
    commitToggle(btn, item);
  }, true);

  function syncHeartStates(){
    var data = read();
    var handles = data.items.map(function(i){ return i.handle; });
    document.querySelectorAll('.gd-wl-heart').forEach(function(btn){
      var h = btn.dataset.handle;
      var active = handles.indexOf(h) > -1;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Save to wishlist');
      setBtnLabel(btn, active);
    });
  }

  // ---- Styles for the compact PDP text-link (injected so this stays one file) ----
  function injectStyles(){
    if(document.getElementById('gd-wl-btn-styles')) return;
    // v7: NOT full-width. Compact inline-flex text-link with brass hairline underline.
    // Positioning resets are still critical because the base .gd-wl-heart class is
    // position:absolute (used for corner hearts on product cards). We reset here
    // so the PDP link renders inline inside the price block.
    var css =
      '.gd-wl-btn--pdp{position:static!important;inset:auto!important;top:auto!important;' +
      'right:auto!important;bottom:auto!important;left:auto!important;transform:none!important;' +
      'display:inline-flex!important;align-items:center;gap:.5em;' +
      'width:auto!important;height:auto!important;margin:10px 0 2px;padding:2px 0;box-sizing:border-box;' +
      'background:transparent;color:#1c1c1c;border:0;border-bottom:1px solid #8C6F42;border-radius:0;box-shadow:none;' +
      'font-family:inherit;font-size:13px;font-weight:400;letter-spacing:.02em;' +
      'text-transform:none;line-height:1.4;cursor:pointer;' +
      'transition:color .15s ease,border-color .15s ease;-webkit-appearance:none;appearance:none;}' +
      '.gd-wl-btn--pdp .gd-wl-heart__icon{width:14px;height:14px;flex:0 0 auto;position:static!important;color:currentColor;}' +
      '.gd-wl-btn--pdp:hover{color:#8C6F42;border-bottom-color:#8C6F42;}' +
      '.gd-wl-btn--pdp.is-active{color:#8C6F42;border-bottom-color:#8C6F42;}' +
      '.gd-wl-btn--pdp.is-active .gd-wl-heart__icon{fill:#8C6F42;stroke:#8C6F42;}' +
      '.gd-wl-btn--pdp:focus-visible{outline:2px solid #8C6F42;outline-offset:3px;}' +
      // v8: the struck list price beside a refreshed clearance price on the wishlist card.
      '.gd-wl-card__was{margin-left:6px;color:#7B6E58;text-decoration:line-through;opacity:.8;font-size:.9em;}';
    var st = document.createElement('style');
    st.id = 'gd-wl-btn-styles';
    st.textContent = css;
    document.head.appendChild(st);
  }

  // ---- PDP: inject a compact "Save to wishlist" link inside the price block ----
  // v7 anchor priority: price-descriptors → price-container → title (fallback).
  // Prefers sitting AFTER the "Free UK delivery over £75" line, INSIDE the price
  // block, so it reads as a companion to the price/delivery info rather than
  // shouting between the title and price.
  function injectProductButton(){
    if(!document.body.classList.contains('template-product')) return;
    var pd = window.gdProductData;
    if(!pd || !pd.handle) return;
    var tries = 0;
    var t = setInterval(function(){
      tries++;
      if(document.querySelector('.gd-wl-btn--pdp')){ clearInterval(t); return; }

      // Preferred anchors — sit inside the price block, below the delivery line.
      var priceHost = document.querySelector(
        '.price-descriptors, .price__descriptors, .price-container__descriptors, ' +
        '.product__price-descriptors, .gd-price__descriptors'
      );
      var priceBlock = document.querySelector(
        '.price-container, .product__price-container, .price, .product__price, ' +
        '.gd-price, .product-single__price'
      );
      // Fallback — same behavior as v6.
      var title = document.querySelector(
        '.product__title, .product__info h1, .product-single__title, .product__details h1, ' +
        '.product-template__title, h1.product-title, .product-info h1'
      ) || document.querySelector('.product__main h1, .product-template h1, main h1');

      var anchor = priceHost || priceBlock || title;
      if(!anchor){ if(tries > 40){ clearInterval(t); } return; }

      var inWl = isIn(pd.handle);
      var btn = document.createElement('button');
      btn.className = 'gd-wl-heart gd-wl-btn--pdp' + (inWl ? ' is-active' : '');
      btn.type = 'button';
      btn.setAttribute('aria-label', inWl ? 'Remove from wishlist' : 'Save to wishlist');
      btn.setAttribute('aria-pressed', inWl ? 'true' : 'false');
      btn.dataset.handle = pd.handle;
      btn.dataset.title  = pd.title;
      btn.dataset.image  = pd.image;
      btn.dataset.price  = pd.price;
      btn.dataset.url    = pd.url;
      btn.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="gd-wl-heart__icon">' +
        '<path d="M12 21s-7.5-4.5-9.5-9.5C1 7 4 4 7 4c2 0 3.5 1 5 3 1.5-2 3-3 5-3 3 0 6 3 4.5 7.5C19.5 16.5 12 21 12 21z"/></svg>' +
        '<span class="gd-wl-btn__label">' + (inWl ? LABEL_SAVED : LABEL_ADD) + '</span>';

      // Insertion strategy:
      // - If anchor is a price descriptor / price container: append inside as the
      //   last child of the price block, so it reads after "Free UK delivery over £75".
      // - Otherwise (title fallback): insert after the title as before.
      if(anchor === priceHost || anchor === priceBlock){
        anchor.appendChild(btn);
      } else {
        var host = anchor.closest('.gd-wl-pdp-title-row') || anchor;
        host.parentNode.insertBefore(btn, host.nextSibling);
      }
      clearInterval(t);
    }, 300);
  }

  function renderWishlistPage(){
    var grid = document.querySelector('[data-gd-wl-grid]');
    if(!grid) return;
    function fmt(p){ return typeof p === 'string' ? p : '£' + (Number(p)/100).toFixed(2); }
    function emptyHTML(){
      return '<div class="gd-wl-empty">' +
        '<p class="gd-wl-empty__title">Your wishlist is empty.</p>' +
        '<p class="gd-wl-empty__text">Tap Save to wishlist on any product to save it here — we will keep it with you.</p>' +
        '<a href="/collections/all" class="gd-wl-empty__browse">Browse the collection &rarr;</a>' +
      '</div>';
    }

    /* v8 — re-derive the CURRENT paid price for every saved item.
       A price stored at save time cannot survive a ladder step, so the stored
       figure is treated as a first paint only. Any failure leaves it on screen. */
    function refreshPrices(items){
      if(!window.gdClearanceCfg) return;   // nothing to recompute against — leave as is
      items.forEach(function(it){
        if(!it || !it.handle) return;
        fetch('/products/' + encodeURIComponent(it.handle) + '.js', {credentials:'same-origin'})
          .then(function(r){ return r.ok ? r.json() : null; })
          .then(function(p){
            if(!p || !p.variants || !p.variants.length) return;
            // Same variant the card prices on: the cheapest one.
            var v = p.variants.slice().sort(function(a,b){ return a.price - b.price; })[0];
            var res = ladder(v.price, v.compare_at_price, p.tags);
            var paidCents = res ? res.cents : v.price;
            var paidText  = moneyFmt(paidCents);
            var el = grid.querySelector('[data-wl-price="' + it.handle + '"]');
            if(el){
              el.innerHTML = paidText + (res
                ? ' <s class="gd-wl-card__was">' + moneyFmt(v.price) + '</s>'
                : '');
            }
            // Quietly correct storage so the server copy and the Klaviyo value stop
            // drifting. Written WITHOUT dispatching gd:wishlist:updated — that event
            // re-renders, which would call this again forever.
            var store = read(), changed = false;
            store.items.forEach(function(si){
              if(si.handle === it.handle && si.price !== paidText){ si.price = paidText; changed = true; }
            });
            if(changed){
              try { localStorage.setItem(KEY, JSON.stringify(store)); } catch(e){}
              if (isLoggedIn) schedulePush();
            }
          })
          .catch(function(){ /* leave the stored price showing */ });
      });
    }

    function render(){
      var data = read();
      if(!data.items.length){
        grid.innerHTML = emptyHTML();
        return;
      }
      grid.innerHTML = data.items.map(function(it){
        return '<div class="gd-wl-card"><a href="'+it.url+'" class="gd-wl-card__image"><img src="'+it.image+'" alt="'+(it.title||'').replace(/"/g,'&quot;')+'" loading="lazy"></a><div class="gd-wl-card__meta"><a href="'+it.url+'" class="gd-wl-card__name">'+it.title+'</a><div class="gd-wl-card__price" data-wl-price="'+it.handle+'">'+fmt(it.price)+'</div><div class="gd-wl-card__actions"><a href="'+it.url+'" class="gd-wl-card__view">View product</a><button class="gd-wl-card__remove" data-remove="'+it.handle+'">Remove</button></div></div></div>';
      }).join('');
      refreshPrices(data.items);
    }
    grid.addEventListener('click', function(e){
      var b = e.target.closest('[data-remove]');
      if(b){ e.preventDefault(); remove(b.dataset.remove); }
    });
    document.addEventListener('gd:wishlist:updated', render);
    render();
  }

  function updateHeaderCount(){
    var n = read().items.length;
    var els = document.querySelectorAll('[data-gd-wl-count]');
    els.forEach(function(el){ el.textContent = n > 0 ? '(' + n + ')' : ''; });
  }

  document.addEventListener('DOMContentLoaded', function(){
    isLoggedIn = document.body.dataset.gdCustomer === 'true';
    injectStyles();
    injectProductButton();
    renderWishlistPage();
    updateHeaderCount();
    syncHeartStates();
    if (isLoggedIn) syncWithServer();
  });
  document.addEventListener('gd:wishlist:updated', function(){
    updateHeaderCount();
    syncHeartStates();
  });

  var dynObserver = new MutationObserver(function(mutations){
    var hasNewHearts = false;
    mutations.forEach(function(m){
      m.addedNodes.forEach(function(n){
        if(n.nodeType === 1 && (n.classList && n.classList.contains('gd-wl-heart') || n.querySelector && n.querySelector('.gd-wl-heart'))){
          hasNewHearts = true;
        }
      });
    });
    if(hasNewHearts) syncHeartStates();
  });
  document.addEventListener('DOMContentLoaded', function(){
    dynObserver.observe(document.body, { childList: true, subtree: true });
    // Re-attempt injection if the buy area renders late (variant/section reloads).
    setTimeout(injectProductButton, 1200);
  });

  window.GDWishlist = { read:read, toggle:toggle, remove:remove, isIn:isIn, syncWithServer:syncWithServer, ladder:ladder };
})();